import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import adminApi from "services/admin-api";
import { formatDateTime, formatMoney, isRecoverableUploadResponseError } from "utils/admin";
import {
  DetailSectionSkeleton,
  PageHeader,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
  StatusPill,
} from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import RightDrawer from "components/global/RightDrawer";
import { DataTable } from "components/molecules/table/table";
import { MerchandiseEditorForm } from "components/admin/forms";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { canManageMerchandiseItem } from "utils/portal-access";
import {
  MerchandiseDetailSecondaryButton,
  MerchandiseDetailPrimaryButton,
  MerchandiseDetailDangerButton,
  MerchandiseDetailSectionSecondary,
  MerchandiseDetailGridLayoutQuaternary,
  MerchandiseDetailSectionTertiary,
  MerchandiseDetailInfoRow,
  MerchandiseDetailGridLayout,
  MerchandiseDetailGridLayoutSecondary,
  MerchandiseDetailContentStack,
  MerchandiseDetailGridLayoutTertiary,
  MerchandiseDetailSection,
  MerchandiseDetailGalleryImage,
  MerchandiseDetailThumbnailImage,
  MerchandiseDetailHeroImage,
  MerchandiseDetailEyebrowText,
  MerchandiseDetailBodyText,
  MerchandiseDetailTitleText,
  MerchandiseDetailMetaText,
  MerchandiseDetailLabelText,
  MerchandiseDetailMetaTextSecondary,
} from "pages/app/merchandise/styles/merchandise-detail.page.inline.styles";
const MerchandiseDetailPage = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const { portalAccess } = useAuth();
  const { data: item, isLoading } = useQuery({
    queryKey: ["merchandise-item", id],
    queryFn: () => adminApi.merchandise.get(id),
    enabled: Boolean(id),
  });
  const { data: athletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
  });
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const effectivePortalAccess = currentUser?.portal_access || portalAccess;
  const canManageItem = canManageMerchandiseItem(effectivePortalAccess, {
    hasLinkedPlayer: Boolean(currentUser?.player?.id),
    itemPlayerId: item?.player_id,
    linkedPlayerId: currentUser?.player?.id,
  });
  const updateMutation = useMutation({
    mutationFn: (payload: any) => {
      if (!canManageItem) {
        return Promise.reject(new Error("You do not have permission to edit this merchandise item."));
      }

      return adminApi.merchandise.update(id, {
        ...payload,
        price: payload.price ? Number(payload.price) : 0,
        stock: payload.stock === "" ? undefined : Number(payload.stock),
      });
    },
    onSuccess: () => {
      toast.success("Merchandise updated.");
      queryClient.invalidateQueries({
        queryKey: ["merchandise-item", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["merchandise"],
      });
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Merchandise updated and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["merchandise-item", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["merchandise"],
        });
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to update this item.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!canManageItem) {
        return Promise.reject(new Error("You do not have permission to delete this merchandise item."));
      }

      return adminApi.merchandise.remove(id);
    },
    onSuccess: () => {
      toast.success("Merchandise deleted.");
      queryClient.invalidateQueries({
        queryKey: ["merchandise"],
      });
      history.push("/merchandise");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to delete this item.");
    },
  });
  return (
    <AppLayout route="/merchandise">
      <PageHeader
        title={item?.name || "Merchandise detail"}
        description="Review storefront presentation, gallery images, metadata, and recommended item overlaps."
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "Merchandise",
            to: "/merchandise",
          },
          {
            label: item?.name || "Item",
          },
        ]}
        actions={
          <>
            <MerchandiseDetailSecondaryButton
              onClick={() => history.push("/merchandise")}
              type="button"
            >
              <FaArrowLeft size={12} />
              Back
            </MerchandiseDetailSecondaryButton>
            {canManageItem ? (
              <>
                <MerchandiseDetailPrimaryButton onClick={() => setDrawerOpen(true)} type="button">
                  <FaEdit size={12} />
                  Edit item
                </MerchandiseDetailPrimaryButton>
                <MerchandiseDetailDangerButton
                  onClick={() => {
                    if (
                      window.confirm("Delete this merchandise item? This action cannot be undone.")
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                  type="button"
                >
                  <FaTrash size={12} />
                  Delete
                </MerchandiseDetailDangerButton>
              </>
            ) : null}
          </>
        }
      />

      {isLoading ? (
        <StatsGridSkeleton />
      ) : (
        <MerchandiseDetailGridLayout>
          <StatCard label="Price" value={item?.formatted_price || formatMoney(item?.price)} />
          <StatCard label="Stock" value={item?.stock ?? "Open"} />
          <StatCard label="Category" value={item?.category || "Uncategorised"} />
          <StatCard
            label="Availability"
            value={<StatusPill value={item?.available ? "active" : "inactive"} />}
          />
        </MerchandiseDetailGridLayout>
      )}

      <MerchandiseDetailGridLayoutSecondary>
        <SectionCard subtitle="Storefront imagery and description" title="Overview">
          {isLoading ? (
            <DetailSectionSkeleton imageHeightClassName="h-80" />
          ) : (
            <MerchandiseDetailContentStack>
              <MerchandiseDetailHeroImage
                alt={item?.name || "Merchandise item"}
                fallbackLabel={item?.name}
                src={item?.image}
                variant="merchandise"
              />
              <MerchandiseDetailGridLayoutTertiary>
                <MerchandiseDetailSection>
                  <MerchandiseDetailEyebrowText>Description</MerchandiseDetailEyebrowText>
                  <MerchandiseDetailBodyText>
                    {item?.long_description ||
                      item?.description ||
                      "No merchandise description has been added for this item yet."}
                  </MerchandiseDetailBodyText>
                </MerchandiseDetailSection>
                <MerchandiseDetailSectionSecondary>
                  <MerchandiseDetailEyebrowText>Athlete</MerchandiseDetailEyebrowText>
                  <MerchandiseDetailTitleText>
                    {item?.player_name || "Unknown"}
                  </MerchandiseDetailTitleText>
                </MerchandiseDetailSectionSecondary>
                <MerchandiseDetailSectionSecondary>
                  <MerchandiseDetailEyebrowText>Team</MerchandiseDetailEyebrowText>
                  <MerchandiseDetailTitleText>
                    {item?.team_name || "Unknown"}
                  </MerchandiseDetailTitleText>
                </MerchandiseDetailSectionSecondary>
              </MerchandiseDetailGridLayoutTertiary>
            </MerchandiseDetailContentStack>
          )}
        </SectionCard>

        <SectionCard subtitle="Gallery and metadata signals" title="Asset Details">
          <MerchandiseDetailContentStack>
            <MerchandiseDetailGridLayoutQuaternary>
              {(item?.gallery_images || [item?.image]).filter(Boolean).map((image: string) => (
                <MerchandiseDetailGalleryImage
                  alt={item?.name || "Merchandise gallery"}
                  fallbackLabel={item?.name}
                  key={image}
                  src={image}
                  variant="merchandise"
                />
              ))}
            </MerchandiseDetailGridLayoutQuaternary>

            <MerchandiseDetailGridLayoutTertiary>
              {Object.entries(item?.metadata || {}).map(([key, value]) => (
                <MerchandiseDetailSectionSecondary key={key}>
                  <MerchandiseDetailEyebrowText>{key}</MerchandiseDetailEyebrowText>
                  <MerchandiseDetailTitleText>{String(value)}</MerchandiseDetailTitleText>
                </MerchandiseDetailSectionSecondary>
              ))}
            </MerchandiseDetailGridLayoutTertiary>

            <MerchandiseDetailMetaText>
              Updated {formatDateTime(item?.updated_at)}.
            </MerchandiseDetailMetaText>
          </MerchandiseDetailContentStack>
        </SectionCard>
      </MerchandiseDetailGridLayoutSecondary>

      <MerchandiseDetailSectionTertiary>
        <SectionCard subtitle="Related items suggested by the backend" title="Recommended Items">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Item",
                accessor: (row: any) => (
                  <MerchandiseDetailInfoRow>
                    <MerchandiseDetailThumbnailImage
                      alt={row.name || "Merchandise item"}
                      fallbackLabel={row.name}
                      src={row.image}
                      variant="merchandise"
                    />
                    <div>
                      <MerchandiseDetailLabelText>{row.name}</MerchandiseDetailLabelText>
                      <MerchandiseDetailMetaTextSecondary>
                        {row.player_name || "Athlete pending"}
                      </MerchandiseDetailMetaTextSecondary>
                    </div>
                  </MerchandiseDetailInfoRow>
                ),
              },
              {
                label: "Category",
                accessor: (row: any) => row.category || "Unknown",
              },
              {
                label: "Price",
                accessor: (row: any) => row.formatted_price || formatMoney(row.price),
              },
              {
                label: "Stock",
                accessor: (row: any) => row.stock ?? "Open",
              },
            ]}
            data={item?.recommended_items || []}
            hideNav
            onView={(recommendedId) => history.push(`/merchandise/${recommendedId}`)}
          />
        </SectionCard>
      </MerchandiseDetailSectionTertiary>

      {canManageItem ? (
        <RightDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} title="Edit item">
          <MerchandiseEditorForm
            initialValues={{
              player_id: item?.player_id || "",
              name: item?.name || "",
              description: item?.description || "",
              long_description: item?.long_description || "",
              price: item?.price ?? "",
              stock: item?.stock ?? "",
              category: item?.category || "jerseys",
              image: item?.image || "",
              gallery_images: item?.gallery_images || [item?.image].filter(Boolean),
              metadata: item?.metadata || {
                signed: true,
                featured: false,
              },
            }}
            isSubmitting={updateMutation.isPending}
            onCancel={() => setDrawerOpen(false)}
            onSubmit={(values) => updateMutation.mutate(values)}
            playerOptions={athletes.map((athlete: any) => ({
              label: athlete.full_name,
              value: athlete.id,
            }))}
            submitLabel="Save changes"
          />
        </RightDrawer>
      ) : null}
    </AppLayout>
  );
};
export default MerchandiseDetailPage;
